import {
  ActionIcon,
  Button,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDeleteBetreuungspreis,
  useBetreuungspreisePlan,
  useUpsertBetreuungspreis,
} from '@/api/useBetreuungspreise';
import {
  useCreateEinnahmeKommune,
  useCreateEinnahmeSonstige,
  useDeleteEinnahmeKommune,
  useDeleteEinnahmeSonstige,
  useEinnahmenKommune,
  useEinnahmenSonstige,
  useFinanzierung,
  useUpsertFinanzierung,
} from '@/api/useFinanzierung';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { MoneyInput } from '@/components/MoneyInput';
import { ParagraphHint } from '@/components/ParagraphHint';
import type {
  FinanzierungAuswahlUpsertBody,
  KommunenModell,
  UUID,
} from '@/api/types';
import { LandAufschluesselungBlock } from './LandAufschluesselungBlock';
import { BETREUUNGSMODELL_VALUES, describeApiError } from './utils';

/** Die fuenf Abschnitte der Finanzierung, verteilt auf drei Reiter. */
export type FinanzierungAbschnitt =
  | 'pauschalen'
  | 'kommune'
  | 'sonstige'
  | 'betreuungspreise';

interface FinanzierungTabProps {
  planId: UUID;
  disabled: boolean;
  onMutated: () => void;
  /**
   * Welche Abschnitte dieser Reiter zeigt.
   *
   * Die Abschnitte teilen sich weder Zustand noch Handler, aber sehr wohl
   * die Fehlerbehandlung und das Nachladen — deshalb bleibt es eine
   * Komponente mit einem Schalter, statt drei Kopien mit dreifach
   * gepflegter Verdrahtung.
   */
  abschnitte: ReadonlyArray<FinanzierungAbschnitt>;
}

const PAUSCHALEN: { key: keyof FinanzierungAuswahlUpsertBody; paragraph: string }[] = [
  { key: 'grund', paragraph: '§ 32 (2) HKJGB' },
  { key: 'gutekita', paragraph: '§ 32 (2a) HKJGB' },
  { key: 'qualitaet', paragraph: '§ 32 (3) HKJGB' },
  { key: 'schwerpunktkita', paragraph: '§ 32 (4) HKJGB' },
  { key: 'integration', paragraph: '§ 32 (5) HKJGB' },
  { key: 'kleinkita', paragraph: '§ 32 (6) HKJGB' },
];

function formatEuro(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(value);
}

function FinanzierungAbschnitte({
  planId,
  disabled,
  onMutated,
  abschnitte,
}: FinanzierungTabProps) {
  const { t, i18n } = useTranslation(['finanzierung', 'betreuungspreise', 'common', 'errors']);
  const zeigt = (a: FinanzierungAbschnitt) => abschnitte.includes(a);

  const auswahl = useFinanzierung(planId);
  const upsertAuswahl = useUpsertFinanzierung(planId);

  const kommune = useEinnahmenKommune(planId);
  const createKommune = useCreateEinnahmeKommune(planId);
  const deleteKommune = useDeleteEinnahmeKommune(planId);

  const sonstige = useEinnahmenSonstige(planId);
  const createSonstige = useCreateEinnahmeSonstige(planId);
  const deleteSonstige = useDeleteEinnahmeSonstige(planId);

  const preise = useBetreuungspreisePlan(planId);
  const upsertPreis = useUpsertBetreuungspreis(planId);
  const deletePreis = useDeleteBetreuungspreis(planId);

  // Local form state for new entries
  const [kommuneForm, setKommuneForm] = useState<{
    modell: KommunenModell;
    betrag: number | null;
    betreuungsmodell: string;
  }>({ modell: 'a', betrag: null, betreuungsmodell: '' });
  const [sonstigeForm, setSonstigeForm] = useState<{
    name: string;
    betrag: number | null;
    monat: number | null;
  }>({ name: '', betrag: null, monat: null });
  const [preisForm, setPreisForm] = useState<string>(BETREUUNGSMODELL_VALUES[0]);
  const [preisLocal, setPreisLocal] = useState<Record<string, { betreuung: number; essen: number }>>(
    {},
  );

  useEffect(() => {
    if (preise.data) {
      const next: Record<string, { betreuung: number; essen: number }> = {};
      for (const row of preise.data) {
        next[row.betreuungsmodell] = {
          betreuung: row.betrag_betreuung,
          essen: row.betrag_essen,
        };
      }
      setPreisLocal(next);
    }
  }, [preise.data]);

  const handleError = (err: unknown) =>
    notifications.show({
      color: 'red',
      title: t('errors:generic'),
      message: describeApiError(err) || t('errors:generic'),
    });

  const togglePauschale = async (
    key: keyof FinanzierungAuswahlUpsertBody,
    next: boolean,
  ) => {
    const current = auswahl.data ?? {
      plan_id: planId,
      grund: false,
      gutekita: false,
      qualitaet: false,
      schwerpunktkita: false,
      integration: false,
      kleinkita: false,
    };
    const body: FinanzierungAuswahlUpsertBody = {
      grund: current.grund,
      gutekita: current.gutekita,
      qualitaet: current.qualitaet,
      schwerpunktkita: current.schwerpunktkita,
      integration: current.integration,
      kleinkita: current.kleinkita,
      [key]: next,
    };
    try {
      await upsertAuswahl.mutateAsync(body);
      notifications.show({ color: 'teal', message: t('finanzierung:info.saved') });
      onMutated();
    } catch (err) {
      handleError(err);
    }
  };

  const submitKommune = async () => {
    if (kommuneForm.betrag === null) return;
    try {
      await createKommune.mutateAsync({
        modell: kommuneForm.modell,
        betrag: kommuneForm.betrag,
        betreuungsmodell:
          kommuneForm.betreuungsmodell.trim() === '' ? null : kommuneForm.betreuungsmodell.trim(),
      });
      notifications.show({ color: 'teal', message: t('finanzierung:info.einnahmeAdded') });
      setKommuneForm({ modell: 'a', betrag: null, betreuungsmodell: '' });
      onMutated();
    } catch (err) {
      handleError(err);
    }
  };

  const submitSonstige = async () => {
    if (sonstigeForm.betrag === null || sonstigeForm.name.trim() === '') return;
    try {
      await createSonstige.mutateAsync({
        name: sonstigeForm.name.trim(),
        betrag: sonstigeForm.betrag,
        monat: sonstigeForm.monat,
      });
      notifications.show({ color: 'teal', message: t('finanzierung:info.einnahmeAdded') });
      setSonstigeForm({ name: '', betrag: null, monat: null });
      onMutated();
    } catch (err) {
      handleError(err);
    }
  };

  const persistPreis = async (modell: string) => {
    const local = preisLocal[modell];
    if (!local) return;
    try {
      await upsertPreis.mutateAsync({
        betreuungsmodell: modell,
        betrag_betreuung: local.betreuung,
        betrag_essen: local.essen,
      });
      notifications.show({ color: 'teal', message: t('betreuungspreise:info.saved') });
      onMutated();
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <Stack>
      {zeigt('pauschalen') ? (
      <Paper withBorder p="md">
        <Title order={4} mb="sm">
          {t('finanzierung:section.pauschalen')}
        </Title>
        {auswahl.isLoading ? <LoadingState height={80} /> : null}
        {auswahl.isError ? <ErrorState error={auswahl.error} /> : null}
        {!auswahl.isLoading ? (
          <Stack gap="xs">
            {PAUSCHALEN.map(({ key, paragraph }) => {
              const checked = auswahl.data?.[key] ?? false;
              return (
                <Group key={key} gap="sm">
                  <Switch
                    checked={checked}
                    label={t(`finanzierung:pauschale.${key}`)}
                    disabled={disabled}
                    onChange={(e) => void togglePauschale(key, e.currentTarget.checked)}
                  />
                  <ParagraphHint paragraph={paragraph} text={t(`finanzierung:pauschale.${key}.hint`)} />
                </Group>
              );
            })}
          </Stack>
        ) : null}
      </Paper>
      ) : null}

      {zeigt('kommune') ? (
      <Paper withBorder p="md">
        <Title order={4} mb="sm">
          {t('finanzierung:section.einnahmenKommune')}
        </Title>
        {kommune.isLoading ? <LoadingState height={80} /> : null}
        {kommune.data && kommune.data.length > 0 ? (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('finanzierung:kommune.modell')}</Table.Th>
                <Table.Th>{t('finanzierung:kommune.betreuungsmodell')}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{t('finanzierung:kommune.betrag')}</Table.Th>
                <Table.Th aria-label={t('common:actions.delete')} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {kommune.data.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{t(`finanzierung:kommune.modellOption.${row.modell}`)}</Table.Td>
                  <Table.Td>{row.betreuungsmodell ?? '—'}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {formatEuro(row.betrag, i18n.language)}
                  </Table.Td>
                  <Table.Td>
                    {!disabled ? (
                      <Tooltip label={t('common:actions.delete')}>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={async () => {
                            try {
                              await deleteKommune.mutateAsync(row.id);
                              notifications.show({
                                color: 'teal',
                                message: t('finanzierung:info.einnahmeDeleted'),
                              });
                              onMutated();
                            } catch (err) {
                              handleError(err);
                            }
                          }}
                          aria-label={t('common:actions.delete')}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    ) : null}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : null}
        {!disabled ? (
          <Group mt="sm" align="flex-end">
            <Select
              label={t('finanzierung:kommune.modell')}
              data={(['a', 'b', 'c', 'd', 'e'] as KommunenModell[]).map((m) => ({
                value: m,
                label: t(`finanzierung:kommune.modellOption.${m}`),
              }))}
              value={kommuneForm.modell}
              onChange={(v) =>
                v && setKommuneForm((s) => ({ ...s, modell: v as KommunenModell }))
              }
              w={140}
            />
            <TextInput
              label={t('finanzierung:kommune.betreuungsmodell')}
              value={kommuneForm.betreuungsmodell}
              onChange={(e) =>
                setKommuneForm((s) => ({ ...s, betreuungsmodell: e.currentTarget.value }))
              }
              w={200}
            />
            <MoneyInput
              label={t('finanzierung:kommune.betrag')}
              value={kommuneForm.betrag}
              onChange={(v) => setKommuneForm((s) => ({ ...s, betrag: v }))}
            />
            <Button
              leftSection={<IconPlus size={14} />}
              onClick={() => void submitKommune()}
              loading={createKommune.isPending}
              disabled={kommuneForm.betrag === null}
            >
              {t('common:actions.add')}
            </Button>
          </Group>
        ) : null}
      </Paper>
      ) : null}

      {/* Die Aufschluesselung steht zwischen Auswahl und freien Zeilen:
          Erst waehlt man die Pauschalen, dann sieht man, was sie bringen,
          und traegt darunter ein, was sonst noch vom Land kommt. */}
      {zeigt('pauschalen') ? (
        <LandAufschluesselungBlock
          planId={planId}
          disabled={disabled}
          onMutated={onMutated}
        />
      ) : null}

      {zeigt('sonstige') ? (
      <Paper withBorder p="md">
        <Title order={4} mb="sm">
          {t('finanzierung:section.einnahmenSonstige')}
        </Title>
        {sonstige.isLoading ? <LoadingState height={80} /> : null}
        {sonstige.data && sonstige.data.length > 0 ? (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('finanzierung:sonstige.name')}</Table.Th>
                <Table.Th>{t('finanzierung:sonstige.monat')}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{t('finanzierung:sonstige.betrag')}</Table.Th>
                <Table.Th aria-label={t('common:actions.delete')} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sonstige.data.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>{row.monat ?? '—'}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {formatEuro(row.betrag, i18n.language)}
                  </Table.Td>
                  <Table.Td>
                    {!disabled ? (
                      <Tooltip label={t('common:actions.delete')}>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={async () => {
                            try {
                              await deleteSonstige.mutateAsync(row.id);
                              notifications.show({
                                color: 'teal',
                                message: t('finanzierung:info.einnahmeDeleted'),
                              });
                              onMutated();
                            } catch (err) {
                              handleError(err);
                            }
                          }}
                          aria-label={t('common:actions.delete')}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    ) : null}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : null}
        {!disabled ? (
          <Group mt="sm" align="flex-end">
            <TextInput
              label={t('finanzierung:sonstige.name')}
              placeholder={t('finanzierung:sonstige.namePlaceholder')}
              value={sonstigeForm.name}
              onChange={(e) => setSonstigeForm((s) => ({ ...s, name: e.currentTarget.value }))}
              w={240}
            />
            <NumberInput
              label={t('finanzierung:sonstige.monat')}
              value={sonstigeForm.monat ?? ''}
              onChange={(v) =>
                setSonstigeForm((s) => ({
                  ...s,
                  monat: v === '' || v === undefined ? null : Number(v),
                }))
              }
              min={1}
              max={12}
              w={120}
              hideControls
            />
            <MoneyInput
              label={t('finanzierung:sonstige.betrag')}
              value={sonstigeForm.betrag}
              onChange={(v) => setSonstigeForm((s) => ({ ...s, betrag: v }))}
            />
            <Button
              leftSection={<IconPlus size={14} />}
              onClick={() => void submitSonstige()}
              loading={createSonstige.isPending}
              disabled={sonstigeForm.betrag === null || sonstigeForm.name.trim() === ''}
            >
              {t('common:actions.add')}
            </Button>
          </Group>
        ) : null}
      </Paper>
      ) : null}

      {zeigt('betreuungspreise') ? (
      <Paper withBorder p="md">
        <Group justify="space-between" mb="sm">
          <Title order={4}>{t('finanzierung:section.betreuungspreise')}</Title>
          <Group align="flex-end">
            <Select
              data={BETREUUNGSMODELL_VALUES.map((v) => ({
                value: v,
                label: t(`belegung:betreuungsmodell.option.${v}`, { defaultValue: v }),
              }))}
              value={preisForm}
              onChange={(v) => v && setPreisForm(v)}
              label={t('betreuungspreise:form.field.modell')}
              w={200}
              disabled={disabled}
            />
            <Button
              leftSection={<IconPlus size={14} />}
              size="xs"
              onClick={() =>
                setPreisLocal((s) => ({
                  ...s,
                  [preisForm]: s[preisForm] ?? { betreuung: 0, essen: 0 },
                }))
              }
              disabled={disabled}
            >
              {t('betreuungspreise:actions.add')}
            </Button>
          </Group>
        </Group>
        <Text size="xs" c="dimmed" mb="xs">
          {t('betreuungspreise:info.autosave')}
        </Text>
        {preise.isLoading ? <LoadingState height={80} /> : null}
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('betreuungspreise:col.modell')}</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>{t('betreuungspreise:col.betreuung')}</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>{t('betreuungspreise:col.essen')}</Table.Th>
              <Table.Th aria-label={t('betreuungspreise:col.aktionen')} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {Object.entries(preisLocal).map(([modell, vals]) => {
              const existing = preise.data?.find((r) => r.betreuungsmodell === modell);
              return (
                <Table.Tr key={modell}>
                  <Table.Td>{modell}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <MoneyInput
                      value={vals.betreuung}
                      onChange={(v) =>
                        setPreisLocal((s) => ({
                          ...s,
                          [modell]: { betreuung: v ?? 0, essen: s[modell]?.essen ?? 0 },
                        }))
                      }
                      onBlur={() => void persistPreis(modell)}
                      disabled={disabled}
                    />
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <MoneyInput
                      value={vals.essen}
                      onChange={(v) =>
                        setPreisLocal((s) => ({
                          ...s,
                          [modell]: { betreuung: s[modell]?.betreuung ?? 0, essen: v ?? 0 },
                        }))
                      }
                      onBlur={() => void persistPreis(modell)}
                      disabled={disabled}
                    />
                  </Table.Td>
                  <Table.Td>
                    {!disabled && existing ? (
                      <Tooltip label={t('betreuungspreise:actions.delete')}>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={async () => {
                            try {
                              await deletePreis.mutateAsync(existing.id);
                              notifications.show({
                                color: 'teal',
                                message: t('betreuungspreise:info.deleted'),
                              });
                              setPreisLocal((s) => {
                                const next = { ...s };
                                delete next[modell];
                                return next;
                              });
                              onMutated();
                            } catch (err) {
                              handleError(err);
                            }
                          }}
                          aria-label={t('betreuungspreise:actions.delete')}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    ) : null}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>
      ) : null}
    </Stack>
  );
}

// --- Die drei Reiter ------------------------------------------------------
//
// Aufteilung nach Geldgeber: Was vom Land kommt (samt der Pauschalen-Auswahl,
// die genau diese Landesmittel steuert), was von der Kommune kommt, und der
// Rest — eigene Einnahmen und die Betreuungspreise, aus denen die
// Elternbeitraege entstehen.

interface ReiterProps {
  planId: UUID;
  disabled: boolean;
  onMutated: () => void;
}

/** Pauschalen-Auswahl und weitere Landesmittel. */
export function EinnahmenLandTab(props: ReiterProps) {
  return <FinanzierungAbschnitte {...props} abschnitte={['pauschalen']} />;
}

/** Kommunale Zuschuesse. */
export function EinnahmenKommuneTab(props: ReiterProps) {
  return <FinanzierungAbschnitte {...props} abschnitte={['kommune']} />;
}

/** Eigene Einnahmen und Betreuungspreise. */
export function EinnahmenSonstigeTab(props: ReiterProps) {
  return (
    <FinanzierungAbschnitte {...props} abschnitte={['sonstige', 'betreuungspreise']} />
  );
}
